import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import Cookies from 'js-cookie';
import { getNotifications, ArchiveNotifications } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const userId = Cookies.get('id');

  // Fetch notifications - can be called manually to refresh
  const fetchNotifications = useCallback(async (force = false) => {
    if (!userId) return;
    
    // Avoid refetching if we already have data and force is false
    if (!force && notifications.length > 0 && lastFetch) {
      const timeSinceLastFetch = Date.now() - lastFetch;
      // Only refetch if more than 5 minutes have passed
      if (timeSinceLastFetch < 5 * 60 * 1000) {
        return;
      }
    }

    try {
      setLoading(true);
      const response = await getNotifications(userId);
      if (response.data && response.data.notifications) {
        setNotifications(response.data.notifications);
        setLastFetch(Date.now());
        
        // Update cookies with notification count (non-archived)
        const nonArchivedCount = response.data.notifications.filter(n => !n.isArchived).length;
        Cookies.set("notifications", JSON.stringify(nonArchivedCount), { expires: 1 });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, notifications.length, lastFetch]);

  // Initial fetch when user logs in
  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [userId]); // Only depend on userId, not fetchNotifications

  // Archive a notification
  const archiveNotification = useCallback(async (notificationId) => {
    try {
      await ArchiveNotifications(userId, notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif._id === notificationId
            ? { ...notif, isArchived: true }
            : notif
        )
      );
      
      // Update cookies with new count
      const newCount = notifications.filter(n => !n.isArchived && n._id !== notificationId).length;
      Cookies.set("notifications", JSON.stringify(newCount), { expires: 1 });
      
      return true;
    } catch (error) {
      console.error('Error archiving notification:', error);
      return false;
    }
  }, [userId, notifications]);

  // Add a new notification (for when one is created)
  const addNotification = useCallback((newNotification) => {
    setNotifications(prev => [newNotification, ...prev]);
    
    // Update cookies with new count
    const newCount = notifications.filter(n => !n.isArchived).length + 1;
    Cookies.set("notifications", JSON.stringify(newCount), { expires: 1 });
  }, [notifications]);

  // Get non-archived notifications
  const getActiveNotifications = useCallback(() => {
    return notifications.filter(n => !n.isArchived);
  }, [notifications]);

  // Get archived notifications
  const getArchivedNotifications = useCallback(() => {
    return notifications.filter(n => n.isArchived);
  }, [notifications]);

  // Get all notifications
  const getAllNotifications = useCallback(() => {
    return notifications.filter(n => !n.isArchived);
  }, [notifications]);

  const value = {
    notifications,
    loading,
    fetchNotifications,
    archiveNotification,
    addNotification,
    getActiveNotifications,
    getArchivedNotifications,
    getAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
